/**
 * Client de l'app de démo → notre PROPRE backend (/api, même origine), qui
 * relaie vers PreLLM. Le navigateur ne voit jamais les plans PreLLM
 * directement : zéro CORS, la clé pk_live ne part qu'au proxy.
 *
 * Auth de l'app : login email/mot de passe → session (cookie). Les URLs des
 * plans sont fixées côté serveur (variables d'env) ; l'app ne manipule que la
 * clé pk_live, changeable à la volée pendant la démo.
 */

export type Tier = 'light' | 'medium' | 'heavy'

export interface SessionUser {
    email?: string
    role?: string
    name?: string
    tenantId?: string
}

export interface SessionState {
    authenticated: boolean
    user: SessionUser | null
    dataPlaneHost: string
}

export interface InspectDetection {
    type: string
    token: string
    original: string
}

export interface InspectData {
    input: string
    sanitized: string
    pii: {
        count: number
        types: string[]
        detections: InspectDetection[]
    }
    injection: {
        detected: boolean
        score: number
    }
}

/** Résultat d'inspection : soit disponible (super_admin), soit dégradé. */
export type InspectOutcome =
    | ({ available: true } & InspectData)
    | { available: false; reason: string }

export interface PrellmHeaders {
    provider?: string
    model?: string
    tier?: string
    fallbackTriggered?: string
    fallbackReason?: string
    piiCount?: string
    piiTypes?: string
    injectionDetected?: string
    tokensInput?: string
    tokensOutput?: string
    latencyMs?: string
    cacheMode?: string
    cacheReadTokens?: string
    cacheCreationTokens?: string
    cacheHitRatio?: string
    quotaState?: string
    quotaRpmUsed?: string
    quotaRpmLimit?: string
    quotaTpmUsed?: string
    quotaTpmLimit?: string
    quotaEurDayUsed?: string
    quotaEurDayLimit?: string
    quotaEurMonthUsed?: string
    quotaEurMonthLimit?: string
    requestId?: string
    tenantId?: string
    apiKeyId?: string
}

export interface CompleteResult {
    ok: boolean
    status: number
    blocked: boolean
    answer: string
    errorMessage: string
    headers: PrellmHeaders
}

/** Levée quand la session a expiré / est absente → l'app doit revenir au login. */
export class AuthError extends Error {}

async function postJson<T>(path: string, payload: unknown): Promise<T> {
    const res = await fetch(path, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
    })
    if (res.status === 401) {
        const d = await res.json().catch(() => null)
        throw new AuthError(d?.message || 'Session expirée')
    }
    if (!res.ok) {
        const d = await res.json().catch(() => null)
        throw new Error(d?.message || `Proxy ${res.status}`)
    }
    return (await res.json()) as T
}

// --- Auth ---------------------------------------------------------------
export async function getSession(): Promise<SessionState> {
    try {
        const res = await fetch('/api/session')
        if (!res.ok) return { authenticated: false, user: null, dataPlaneHost: '' }
        return (await res.json()) as SessionState
    } catch {
        return { authenticated: false, user: null, dataPlaneHost: '' }
    }
}

export async function login(email: string, password: string): Promise<SessionState> {
    const res = await fetch('/api/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, password }),
    })
    const d = await res.json().catch(() => null)
    if (!res.ok) throw new Error(d?.message || 'Identifiants invalides')
    return { authenticated: true, user: d.user, dataPlaneHost: d.dataPlaneHost }
}

export async function logout(): Promise<void> {
    await fetch('/api/logout', { method: 'POST' }).catch(() => {})
}

// --- Data-plane (session requise) --------------------------------------
export function inspect(apiKey: string, text: string): Promise<InspectOutcome> {
    return postJson<InspectOutcome>('/api/inspect', { apiKey, text })
}

export function complete(apiKey: string, text: string, tier: Tier): Promise<CompleteResult> {
    return postJson<CompleteResult>('/api/complete', { apiKey, text, tier })
}
